import { jsPDF } from 'jspdf';
import { MONTSERRAT_REGULAR, MONTSERRAT_BOLD } from './montserrat-fonts';
import {
    renderSavingsPlanPage,
    renderGoalsPage,
    renderFinalPage,
    renderIssue1Insurance,
    renderIssue2Emergency,
    renderIssue3Savings,
    renderIssue4Debt,
    renderIssue5Tax,
    renderRoadmapPage,
    renderGlossaryPage,
    renderGlossaryPage2,
    renderGlossaryPage3,
    renderGlossaryPage4,
    renderGlossaryPage5,
    renderGlossaryPage6,
    renderParametersPage,
    renderDisclaimerPage,
    renderRoadmapSummaryPage,
    renderSnapshotPage,
    renderDiagnosisPage,
    renderStrengthsPage1,
    renderStrengthsPage2,
    renderFiveGapsPage,
    renderAttentionPage,
    renderAttentionPage2,
    renderTimelinePage,
    renderSavingsOptimisationPage,
    renderWealthLiabilitiesPage,
    addFooter,
    addSimpleFooter,
    renderGoalBasedInvestmentPlan,
    renderJointTaxOptimisationPage,
    renderJointDebtAndGoalsPage
} from './pdfContent';

export const generateProfessionalPDF = (report, calculations) => {
    if (!report || !calculations) return;

    const isBusiness = calculations.incomeType === 'business';

    // 1. Password Protection (Last 4 digits of Phone)
    const rawPhone = report.personalInfo?.phone || report.personalInfo?.contactNo || "";
    const cleanPhone = rawPhone.toString().replace(/\D/g, '');
    const userPassword = cleanPhone.length >= 4 ? cleanPhone.slice(-4) : (cleanPhone || "1234");

    console.log("Setting PDF Password:", userPassword);

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    let doc;
    try {
        const config = {
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            putOnlyUsedFonts: true,
            compress: true,
            // Password protection for all devices (phone last 4 digits)
            encryption: {
                userPassword: userPassword.toString(),
                ownerPassword: userPassword.toString(),
                userPermissions: ["print", "modify", "copy", "annot-forms"]
            }
        };

        doc = new jsPDF(config);
    } catch (err) {
        console.warn("jsPDF init failed, using fallback:", err);
        // Try with encryption
        try {
            doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
                encryption: {
                    userPassword: userPassword.toString(),
                    ownerPassword: userPassword.toString(),
                    userPermissions: ["print", "modify", "copy", "annot-forms"]
                }
            });
        } catch (encryptErr) {
            console.warn("Encryption failed, creating without password:", encryptErr);
            doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
        }
    }

    // CRASH-PROOF FONT REGISTRATION
    let fontName = 'helvetica';
    try {
        if (MONTSERRAT_REGULAR && MONTSERRAT_REGULAR.length > 1000) {
            doc.addFileToVFS('Montserrat-Regular.ttf', MONTSERRAT_REGULAR);
            doc.addFont('Montserrat-Regular.ttf', 'Montserrat', 'normal');
            doc.setFont('Montserrat', 'normal');
            fontName = 'Montserrat';
        }
        if (MONTSERRAT_BOLD && MONTSERRAT_BOLD.length > 1000) {
            doc.addFileToVFS('Montserrat-Bold.ttf', MONTSERRAT_BOLD);
            doc.addFont('Montserrat-Bold.ttf', 'Montserrat', 'bold');
        }
    } catch (fontErr) {
        console.warn("Font registration failed, using fallback:", fontErr);
        doc.setFont('helvetica', 'normal');
        fontName = 'helvetica';
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const personalInfo = report.personalInfo || {};
    const metrics = calculations.metrics || {};
    const m = metrics;
    const grades = calculations.grades || {};
    const score = grades.overall || 65;

    const safeParse = (v) => {
        if (typeof v === 'number') return v;
        if (!v) return 0;
        return parseFloat(v.toString().replace(/[₹, \s]/g, '')) || 0;
    };

    // --- RECALCULATE SURPLUS TO EXACTLY MATCH PAGE 3 UNASSIGNED SURPLUS ---
    const isBiz = report?.incomeType === 'business' || calculations?.incomeType === 'business';
    const sInc = (isBiz ? report?.businessIncome : report?.salaryIncome) || {};
    const sExp = (isBiz ? report?.businessExpenses : report?.salaryExpenses) || {};
    const sInv = (isBiz ? report?.businessInvestment : report?.salaryInvestment) || {};
    const section3 = report?.section3 || {};
    const hl = safeParse(section3.homeLoanEMI) || (safeParse(sInc.homeLoanEMI) || 0);
    const rt = (safeParse(sInc.rentEMI) || 0);
    const lv = (safeParse(sInc.societyMaintenance) || 0) + (safeParse(sInc.propertyTax) || 0);
    const gr = (safeParse(sExp.groceries) || 0) + (safeParse(sExp.vegetables) || 0) + (safeParse(sExp.dairyProducts) || 0) + (safeParse(sExp.householdSupplies) || 0);
    const medical = (safeParse(sExp.doctorConsultations) || 0) + (safeParse(sExp.medicines) || 0) + (safeParse(sExp.diagnosticTests) || 0) + (safeParse(sExp.dentalCare) || 0) + (safeParse(sExp.eyeCare) || 0);
    const education = (safeParse(sExp.schoolFees) || 0) + (safeParse(sExp.booksStationery) || 0) + (safeParse(sExp.tuitionClasses) || 0) + (safeParse(sExp.extracurricular) || 0);
    const ut = (safeParse(sInc.electricity) || 0) + (safeParse(sInc.water) || 0) + (safeParse(sInc.gas) || 0) + (safeParse(sInc.internet) || 0) + (safeParse(sInc.broadband) || 0) + (safeParse(sInc.ottSubscriptions) || 0) + (safeParse(sInc.cable) || 0) + medical + education;
    const tr = (safeParse(sExp.fuelDiesel) || 0) + (safeParse(sExp.publicTransport) || 0) + (safeParse(sExp.taxiCabUber) || 0) + (safeParse(sExp.vehicleMaintenance) || 0) + (safeParse(sExp.parkingCharges) || 0) + (safeParse(sExp.tollCharges) || 0);
    const pl = safeParse(section3.personalLoanEMI) || (safeParse(sInc.personalLoanEMI) || 0) + (safeParse(sInc.carLoanEMI) || 0) + (safeParse(sInc.educationLoanEMI) || 0) + (safeParse(sInc.creditCardEMI) || 0) + (safeParse(sInc.otherLoanEMI) || 0);
    const in_ = (safeParse(sInc.lifeInsurance) || 0) + (safeParse(sInc.healthInsurance) || 0) + (safeParse(sInc.criticalIllness) || 0) + (safeParse(sInc.motorInsurance) || 0) + (safeParse(sInc.homeInsurance) || 0);
    const di = (safeParse(sInv.diningOut) || 0) + (safeParse(sInv.entertainment) || 0) + (safeParse(sInv.moviesTheater) || 0);
    const shExpKeys = ['salonBarber', 'cosmeticsAmount', 'clothingFootwearAmount'];
    const shInvKeys = ['sportsHobbies', 'clubMemberships', 'shopping', 'electronics', 'furnitureDecor', 'apparelAccessories', 'giftsDonations', 'travel', 'vacationPlanning', 'dailyOfficeCommute', 'parentingSupport', 'childrenAllowance', 'domesticHelp', 'weddingContributions', 'festivalExpenses', 'religiousDonations', 'charityNGO', 'birthdayCelebrations', 'anniversaryCelebrations', 'familyFunctions', 'professionalCourses', 'certificationFees', 'booksJournals', 'workFromHome', 'professionalMembership', 'officeCabTravel', 'businessMeals'];
    const sh = shExpKeys.reduce((acc, k) => acc + (safeParse(sExp[k]) || 0), 0) + shInvKeys.reduce((acc, k) => acc + (safeParse(sInv[k]) || 0), 0);
    const em = (safeParse(sInv.emergencyFund) || 0) + (safeParse(sInv.liquidFund) || 0);
    const pf = (safeParse(sInc.ppf) || 0) + (safeParse(sInc.sukanyaSamriddhi) || 0);
    const ef = (safeParse(sInc.epf) || 0) + (safeParse(sInc.vpf) || 0) + (safeParse(sInc.nps_80ccd2) || 0);
    const mf = (safeParse(sInv.mutualFunds) || 0) + (safeParse(sInv.stocksEquity) || 0) + (safeParse(sInv.recurringDeposits) || 0) + (safeParse(sInv.fixedDeposits) || 0) + (safeParse(sInv.goldBonds) || 0);
    const trackedVal = (hl || rt) + lv + gr + ut + tr + pl + in_ + di + sh + em + pf + mf + ef;

    metrics.monthlySurplus = Math.max(0, (metrics.totalMonthlyIncome || 0) - trackedVal);
    m.monthlySurplus = metrics.monthlySurplus;
    // --------------------------------------------------------------------------

    // ── FINANCIAL CALCULATIONS & DATA MAPPING (TOP-LEVEL) ─────────────────
    const isCouple = personalInfo.familyType === 'couple';
    const jointSalary = report.jointSalary || {};

    const capitalize = (s) => s ? s.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : "";

    let displayName = capitalize(personalInfo.name || personalInfo.firstName + " " + personalInfo.lastName || "Client");
    let salutationName = displayName.split(' ')[0];
    let displayAge = `${personalInfo.age || ''} Years`;
    let introText = "You just did something that most people your age never do — you stopped to look honestly at your financial life. That single act of courage and curiosity puts you miles ahead of the crowd.";

    if (isCouple) {
        const hName = capitalize(jointSalary.sectionA?.husbandName || "");
        const wName = capitalize(jointSalary.sectionA?.wifeName || "");
        displayName = `${hName} & ${wName}`;

        const hFirstName = hName.split(' ')[0];
        const wFirstName = wName.split(' ')[0];
        salutationName = `${hFirstName} & ${wFirstName}`;

        const hAge = jointSalary.sectionA?.husbandAge || "";
        const wAge = jointSalary.sectionA?.wifeAge || "";
        displayAge = `${hAge} Yrs (Husband) & ${wAge} Yrs (Wife)`;

        introText = "You just did something that most couples your age never do — you stopped to look honestly at your financial life. That single act of courage and curiosity puts you miles ahead of the crowd.";
    }

    const name = salutationName;
    const monthlyIncome = metrics.totalMonthlyIncome || 0;
    const annualIncome = monthlyIncome * 12;
    const monthlyExpenses = (metrics.totalEssentialExpenses || 0) + (metrics.totalLifestyleExpenses || 0);
    // Emergency fund — use engine output if available, else derive from expenses
    const fallbackExp = (metrics.totalMonthlyIncome || 0) * 0.4;
    const emergencyFundTarget = metrics.emergencyFundTarget || metrics.targetEmergency || Math.round((metrics.totalEssentialExpenses || fallbackExp || 30000) * 6);
    const emergencyFundActual = metrics.currentEmergency || safeParse(report?.section4?.emergencyFundAmount) || metrics.totalLiquid || 0;
    const savingsRateActual = (metrics.savingsRate || 0).toFixed(1);
    const lifeInsuranceActual = metrics.currentLifeCover || calculations.lifeInsurance?.totalCover || safeParse(report?.section3?.lifeInsuranceCover) || 0;
    const lifeInsuranceTarget = Math.round(annualIncome * 10);
    const lifeInsuranceActualL = (lifeInsuranceActual / 100000).toFixed(0);
    const lifeInsuranceTargetCr = (lifeInsuranceTarget / 10000000).toFixed(1);
    // Personal loan — prefer raw report data, fall back to metrics
    const personalLoanBalance = safeParse(report?.salaryIncome?.personalLoanOutstanding) || calculations.personalLoan?.balance || metrics.personalLoanBalance || 0;
    const personalLoanEMI = safeParse(report?.salaryIncome?.personalLoanEMI) || metrics.personalLoanEMI || 0;
    // Tax savings — use engine's old vs new regime comparison
    const taxSavings = metrics.tax?.taxSavings || calculations.taxSavings || 0;
    const taxSavingsMonthly = Math.round(taxSavings / 12);
    const deductions = metrics.tax?.oldRegime?.totalDeductions || calculations.deductions || 0;
    // Rent & city — pull directly from user input
    const rentPaid = safeParse(report?.salaryIncome?.rentEMI) || calculations.rentPaid || 0;
    const cityType = personalInfo.city || personalInfo.cityType || calculations.cityType || "Metro";
    const retirementTarget = Math.round(annualIncome * 15 / 100000) * 100000;
    const retirementCorpus = Math.round(retirementTarget * 1.2);
    const retirementCorpusCr = (retirementCorpus / 10000000).toFixed(2);
    const emergencyFundTargetL = (emergencyFundTarget / 100000).toFixed(1);
    const surplusLoss5y = Math.round(((annualIncome * 0.20) - (annualIncome * safeParse(savingsRateActual) / 100)) * 5 * 1.3 / 100000) * 100000;
    const surplusLossCr = (surplusLoss5y / 10000000).toFixed(1);

    const formatCurrency = (value) => {
        const val = Math.abs(value || 0);
        const formatted = new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
        }).format(val);
        return (value < 0 ? "-" : "") + formatted;
    };

    const addHeader = (doc, section, title) => {
        doc.setFillColor(6, 150, 104); doc.roundedRect(14, 10, 45, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont(fontName, "bold");
        doc.text(section, 18, 14);

        doc.setTextColor(30, 41, 59); doc.setFontSize(12); doc.setFont(fontName, "bold");
        doc.text(title?.toUpperCase() || "", 14, 25);

        doc.setDrawColor(241, 245, 249); doc.setLineWidth(0.5); doc.line(14, 28, pageWidth - 14, 28);
        doc.setTextColor(30, 41, 59); doc.setFontSize(8); doc.setFont(fontName, "normal");
        doc.text("DIGISAHYADRI ECONOMIC STABILITY SYSTEM", pageWidth - 14, 25, { align: 'right' });
    };

    // Redundant addPageNumberBadge function removed

    // --- PAGE 1: COVER PAGE (1:1 IMAGE MATCH) ---
    doc.setFillColor(255, 255, 255); doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Brand Header
    try {
        doc.addImage('/Yathartha-Logo.png', 'PNG', 14, 8, 40, 8);
    } catch (e) {
        doc.setFontSize(12); doc.setTextColor(6, 150, 104); doc.setFont("Montserrat", "bold");
        doc.text("YATHARTHA", 14, 18);
    }

    // Light Grey Title Capsule
    const titleBoxY = 32;
    doc.setFillColor(241, 245, 249); doc.roundedRect(10, titleBoxY, pageWidth - 20, 62, 8, 8, 'F');

    doc.setTextColor(30, 41, 59); doc.setFontSize(12.5); doc.setFont("Montserrat", "normal");
    doc.text("Your Personal Finance Companion, Built for Real Indians", 18, titleBoxY + 8);

    doc.setTextColor(6, 150, 104); doc.setFontSize(28); doc.setFont("Montserrat", "bold");
    doc.text("COMPREHENSIVE FINANCIAL", 18, titleBoxY + 24);
    doc.text("DIAGNOSIS REPORT", 18, titleBoxY + 36);

    doc.setFillColor(6, 150, 104); doc.rect(18, titleBoxY + 41, 25, 1.2, 'F');

    doc.setTextColor(30, 41, 59); doc.setFontSize(16); doc.setFont("Montserrat", "normal");
    doc.text("With Financial Education, Glossary &", 18, titleBoxY + 50);
    doc.text("Personalised Roadmap", 18, titleBoxY + 57);

    // Profile Icons + Details
    const iconX = 14;
    const labelX = 28;
    const drawRow = (iconFunc, label, value, y) => {
        iconFunc(iconX, y - 4);
        doc.setTextColor(30, 41, 59); doc.setFontSize(13.5); doc.setFont("Montserrat", "bold");
        doc.text(label, labelX, y);
        doc.setTextColor(6, 150, 104); doc.setFontSize(20); doc.setFont("Montserrat", "bold");
        doc.text(value, labelX, y + 8);
    };

    const userIcon = (x, y) => { try { doc.addImage('/icons/user.png', 'PNG', x, y, 10, 10); } catch (e) { } };
    const portIcon = (x, y) => { try { doc.addImage('/icons/briefcase.png', 'PNG', x, y, 10, 10); } catch (e) { } };
    const locIcon = (x, y) => { try { doc.addImage('/icons/pin.png', 'PNG', x, y, 10, 10); } catch (e) { } };
    const scoreIcon = (x, y) => { try { doc.addImage('/icons/score.png', 'PNG', x, y, 10, 10); } catch (e) { } };

    drawRow(userIcon, "Prepared For", displayName.toUpperCase(), 115);
    drawRow(portIcon, "Profession", isBusiness ? "BUSINESS PROFESSIONAL" : "SALARIED PROFESSIONAL", 142);
    drawRow(locIcon, "Age", displayAge.toUpperCase(), 169);
    drawRow(scoreIcon, "Overall Score", `${grades.overall || score} / 100 | Target: 80+`, 196);

    // Bottom Green Capsule
    const msgY = 212;
    doc.setFillColor(6, 150, 104); doc.roundedRect(10, msgY, pageWidth - 20, 65, 8, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
    doc.text(`Dear ${salutationName},`, 18, msgY + 10);
    doc.setFontSize(13.5); doc.setFont("Montserrat", "normal");
    const msg = [
        introText,
        "Your score today is " + (grades.overall || score) + "/100. By the time you finish reading this report and take the first three actions, you will already feel more in control of your financial life than ever before.",
        "Let's build the future you deserve. Together."
    ];
    doc.text(msg[0], 18, msgY + 18, { maxWidth: pageWidth - 36 });
    doc.text(msg[1], 18, msgY + 38, { maxWidth: pageWidth - 36 });
    doc.setFont("Montserrat", "bold"); doc.text(msg[2], 18, msgY + 58);

    // --- PAGE 2: TABLE OF CONTENTS (IMAGE MATCH 1:1) ---
    doc.addPage();

    // Header
    doc.setTextColor(30, 41, 59); doc.setFontSize(28); doc.setFont("Montserrat", "bold");
    doc.text("TABLE OF", 14, 25);
    doc.setTextColor(6, 150, 104);
    doc.text("CONTENTS", 70, 25);
    try { doc.addImage('/Yathartha-Logo.png', 'PNG', pageWidth - 54, 8, 40, 8); } catch (e) { }
    doc.setDrawColor(229, 231, 235); doc.setLineWidth(1.5); doc.line(14, 32, 28, 32);

    const sections = [
        { s: "01", t: "Executive Summary & Gaps Analysis", p: "01" },
        { s: "02", t: "The Important Gaps That Need Your Attention", p: "02" },
        { s: "03", t: "Your Financial Snapshot at a Glance", p: "03" },
        { s: "04", t: "Six-Parameter Comprehensive Diagnosis", p: "04" },
        { s: "05", t: "What Is Working Well For You", p: "05" },
        { s: "06", t: "Detailed Issue Analysis (Insurance to Debt)", p: "07" },
        { s: "07", t: "Your Personal Roadmap \u2014 Step by Step", p: "10" },
        { s: "08", t: "Goal-Based Investment Plan", p: "13" },
        { s: "09", t: "Master Action Checklist", p: "14" },
        { s: "10", t: "Financial Glossary \u2014 25 Terms You Must Know", p: "15" }
    ];

    let ty = 42;
    sections.forEach(item => {
        // Main Border
        doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.4);
        doc.roundedRect(14, ty, pageWidth - 28, 12, 4, 4, 'S');

        // Green Cap
        doc.setFillColor(6, 150, 104); doc.roundedRect(14, ty, 35, 12, 4, 4, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(5); doc.setFont("Montserrat", "bold");
        doc.text("SEC", 17, ty + 5); doc.text("TION", 17, ty + 8);
        doc.setFontSize(22); doc.text(item.s, 24, ty + 9);

        // Title
        doc.setTextColor(30, 41, 59); doc.setFontSize(13.5); doc.setFont("Montserrat", "bold");
        doc.text(item.t, 55, ty + 7.5);

        // Vertical Bar & Page
        doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.8); doc.line(pageWidth - 32, ty + 3, pageWidth - 32, ty + 9);
        doc.setTextColor(30, 41, 59); doc.setFontSize(12.5);
        doc.text(item.p, pageWidth - 24, ty + 8, { align: 'center' });

        ty += 15.5;
    });

    // Footer removed as per user request
    doc.addPage();
    // Section Header
    doc.setFillColor(6, 150, 104); doc.roundedRect(14, 10, 45, 6, 1, 1, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("Montserrat", "bold");
    doc.text("SECTION 1", 18, 14);

    doc.setTextColor(6, 150, 104); doc.setFontSize(isCouple ? 20 : 26); doc.setFont("Montserrat", "bold");
    doc.text(isCouple ? "EXECUTIVE SUMMARY (Joint Family Edition)" : "EXECUTIVE SUMMARY", 14, 25);
    doc.setTextColor(30, 41, 59); doc.setFontSize(12.5); doc.setFont("Montserrat", "normal");
    const reportDate = new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    doc.text(`Your Financial Story at a Glance - ${displayName.toUpperCase()} | ${reportDate}`, 14, 32);

    // Narrative calculations

    const p = personalInfo;
    const pNameForSummary = salutationName;
    const genderPronoun = p.gender === 'female' ? 'She' : (p.gender === 'male' ? 'He' : 'The professional');
    const possessivePronoun = p.gender === 'female' ? 'Her' : (p.gender === 'male' ? 'His' : 'Their');

    // Dynamic checks for user data
    // Dynamic checks for user data (Robust detection)
    const hEMI = parseFloat(report?.salaryIncome?.homeLoanEMI) || parseFloat(metrics?.homeLoanEMI) || 0;
    const hOut = parseFloat(report?.landGold?.homeLoanOutstanding) || parseFloat(report?.salaryIncome?.homeLoanOutstanding) || parseFloat(metrics?.homeLoanOutstanding) || 0;
    const hasHomeLoan = hEMI > 0 || hOut > 0;
    const healthInsuranceValue = parseFloat(report?.salaryIncome?.healthInsurance) || 0;
    const healthInsurancePremiumValue = parseFloat(metrics?.healthInsurancePremium) || 0;
    const hasHealthInsurance = healthInsuranceValue > 0 || healthInsurancePremiumValue > 0;

    console.log('DEBUG - Health Insurance:', {
        healthInsuranceValue,
        healthInsurancePremiumValue,
        hasHealthInsurance,
        rawHealthInsurance: report?.salaryIncome?.healthInsurance,
        rawHealthInsurancePremium: metrics?.healthInsurancePremium
    });

    const hasInvestments = (parseFloat(report?.salaryInvestment?.mutualFunds) || 0) > 0 ||
        (parseFloat(report?.salaryIncome?.ppf) || 0) > 0 ||
        (parseFloat(report?.salaryIncome?.epf) || 0) > 0 ||
        (parseFloat(report?.salaryInvestment?.sip) || 0) > 0 ||
        (parseFloat(metrics?.sip) || 0) > 0 ||
        (parseFloat(metrics?.ppf) || 0) > 0;

    let userSummaryText = "";
    let coupleStrengths = [];
    if (isCouple) {
        const cleanCityName = (val) => {
            if (!val) return "";
            const lower = val.toString().toLowerCase().trim();
            if (lower === "metro" || lower === "non_metro" || lower === "non-metro" || lower === "tier2" || lower === "tier3" || lower === "rural") {
                if (lower === "tier2") return "Tier 2";
                if (lower === "tier3") return "Tier 3";
                if (lower === "non_metro" || lower === "non-metro") return "Non-Metro";
                return val.toString().charAt(0).toUpperCase() + val.toString().slice(1);
            }
            return val.toString().replace(/(?:\s*\()?metro\)?/i, "").trim();
        };

        const sectionA = jointSalary.sectionA || {};
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        const hName = (sectionA.husbandName || "Husband").trim().split(' ')[0];
        const wName = (sectionA.wifeName || "Wife").trim().split(' ')[0];
        const hAge = sectionA.husbandAge || "30";
        const wAge = sectionA.wifeAge || "30";

        const getProf = (salary, otherInc, otherIncType) => {
            const hasSal = parseFloat(salary) > 0;
            const hasOth = parseFloat(otherInc) > 0;
            if (hasSal && hasOth && otherIncType) return `salaried + ${otherIncType.toLowerCase()}`;
            if (hasSal) return 'salaried';
            if (hasOth && otherIncType) return otherIncType.toLowerCase();
            return 'salaried';
        };

        const hProf = getProf(sectionB.husbandSalary, sectionB.husbandOtherIncome, sectionB.husbandOtherIncomeType);
        const wProf = getProf(sectionB.wifeSalary, sectionB.wifeOtherIncome, sectionB.wifeOtherIncomeType);

        const formatLakh = (val) => {
            const num = parseFloat(val) || 0;
            if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, '')}L/month`;
            if (num >= 1000) return `₹${(num / 1000).toFixed(0)}k/month`;
            return `₹${num}/month`;
        };

        const hIncStr = formatLakh(sectionB.husbandSalary || sectionB.husbandOtherIncome);
        const wIncStr = formatLakh(sectionB.wifeSalary || sectionB.wifeOtherIncome);

        const isDual = (parseFloat(sectionB.husbandSalary) > 0 || parseFloat(sectionB.husbandOtherIncome) > 0) &&
            (parseFloat(sectionB.wifeSalary) > 0 || parseFloat(sectionB.wifeOtherIncome) > 0);
        const coupleType = isDual ? "dual-income couple" : "single-income couple";
        const city = cleanCityName(sectionA.cityType || personalInfo.cityType || personalInfo.city || personalInfo.address || "Pune");

        const homeLoanEMI = parseFloat(sectionC.homeLoanEMI) || parseFloat(sectionF.homeLoanEMI) || 0;
        const hasHomeLoan = homeLoanEMI > 0 || parseFloat(sectionF.homeLoanOutstanding) > 0;
        const rentPaid = parseFloat(sectionC.rent) || 0;
        const homePart = hasHomeLoan
            ? "own a joint home (with a loan)"
            : (rentPaid > 0 ? "live in a rented home" : "are building their financial foundation");

        const hasHealthIns = (parseFloat(sectionE.husbandHealthCover) > 0 || parseFloat(sectionE.wifeHealthCover) > 0 || parseFloat(sectionE.husbandHealthPremium) > 0 || parseFloat(sectionE.wifeHealthPremium) > 0 || parseFloat(sectionC.healthInsurance) > 0);
        const healthPart = hasHealthIns ? "have health insurance" : "have basic health protection";

        const activeInvest = [];
        if (parseFloat(sectionD.husbandEPF) > 0 || parseFloat(sectionD.wifeEPF) > 0) activeInvest.push("EPF");
        if (parseFloat(sectionD.husbandPPF) > 0 || parseFloat(sectionD.wifePPF) > 0) activeInvest.push("PPF");
        if (parseFloat(sectionD.husbandMutualFund) > 0 || parseFloat(sectionD.wifeMutualFund) > 0 || parseFloat(sectionD.husbandELSS) > 0 || parseFloat(sectionD.wifeELSS) > 0) activeInvest.push("Mutual Funds");
        if (parseFloat(sectionD.husbandNPS) > 0 || parseFloat(sectionD.wifeNPS) > 0) activeInvest.push("NPS");
        if (parseFloat(sectionD.husbandFD) > 0 || parseFloat(sectionD.wifeFD) > 0) activeInvest.push("FDs");

        let savePart = "";
        if (activeInvest.length === 0) {
            savePart = "are planning to start saving";
        } else if (activeInvest.length === 1) {
            savePart = `save via ${activeInvest[0]}`;
        } else if (activeInvest.length === 2) {
            savePart = `save via ${activeInvest[0]} and ${activeInvest[1]}`;
        } else {
            savePart = `save via ${activeInvest.slice(0, -1).join(', ')}, and ${activeInvest[activeInvest.length - 1]}`;
        }

        const combinedIncomeNum = (parseFloat(sectionB.husbandSalary) || 0) + (parseFloat(sectionB.husbandOtherIncome) || 0) +
            (parseFloat(sectionB.wifeSalary) || 0) + (parseFloat(sectionB.wifeOtherIncome) || 0);

        const formatCombinedLakh = (val) => {
            const num = parseFloat(val) || 0;
            if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, '')} lakhs`;
            return `₹${num.toLocaleString('en-IN')}`;
        };
        const combinedIncomeStr = formatCombinedLakh(combinedIncomeNum);

        const gaps = [];
        const lifeCover = (parseFloat(sectionE.husbandLifeCover) || 0) + (parseFloat(sectionE.wifeLifeCover) || 0);
        if (lifeCover <= 0) gaps.push("zero life insurance");

        const emergency = parseFloat(sectionG.emergencyFund) || 0;
        if (emergency <= 0) gaps.push("no emergency fund");

        const personalLoanOutstanding = parseFloat(sectionF.personalLoanOutstanding) || 0;
        if (personalLoanOutstanding > 0) {
            const rate = sectionF.personalLoanRate || "15";
            const amt = personalLoanOutstanding >= 100000
                ? `₹${(personalLoanOutstanding / 100000).toFixed(1).replace(/\.0$/, '')}L`
                : `₹${(personalLoanOutstanding / 1000).toFixed(0)}k`;
            gaps.push(`a joint personal loan of ${amt} at ${rate}%`);
        }

        const gapsStr = gaps.length > 0
            ? `They are disciplined but have critical gaps: ${gaps.join(', ')}.`
            : "They are disciplined and have set up their basic protections well.";

        userSummaryText = `${hName} (${hAge}, ${hProf}, ${hIncStr}) and ${wName} (${wAge}, ${wProf}, ${wIncStr}) are a ${coupleType} in ${city}. They ${homePart}, ${healthPart}, and ${savePart}. Their combined monthly income is ${combinedIncomeStr}. ${gapsStr} Their financial health score today is ${grades.overall || score}/100. This report shows exactly how to move to 80+.`;

        // Populate coupleStrengths
        // 1. Dual Income Stability
        if (isDual) {
            coupleStrengths.push({ t: "Dual Income Stability", d: "Two earners mean one job loss does not break you.", i: '/icons/partnerships.png' });
        } else {
            coupleStrengths.push({ t: "Single Income Focus", d: "A steady income source is the foundation of your plan.", i: '/icons/partnerships.png' });
        }

        // 2. Low Lifestyle Spending
        const lifestylePct = parseFloat(metrics.lifestyleRatio || 0);
        if (lifestylePct <= 20) {
            coupleStrengths.push({ t: `Low Lifestyle Spending (${lifestylePct.toFixed(0)}%)`, d: "You enjoy life without overspending.", i: '/icons/piggy-bank.png' });
        } else {
            coupleStrengths.push({ t: `Lifestyle Spending Control (${lifestylePct.toFixed(0)}%)`, d: "Keeping lifestyle spending optimized is key to your wealth strategy.", i: '/icons/piggy-bank.png' });
        }

        // 3. Home Loan
        const homeLoanOutstanding = parseFloat(sectionF.homeLoanOutstanding) || 0;
        const principalRatio = homeLoanOutstanding > 0 ? Math.min(0.55, Math.max(0.35, (homeLoanEMI * 12) / (homeLoanOutstanding + homeLoanEMI * 120))) : 0.5;
        const annualPrincipal = homeLoanEMI * 12 * principalRatio;
        const section24Interest = Math.min(400000, homeLoanEMI * 12 * (1 - principalRatio));
        const section80CPrincipal = Math.min(300000, annualPrincipal);
        const totalHomeDeduction = section24Interest + section80CPrincipal;
        if (hasHomeLoan) {
            coupleStrengths.push({ t: "Home Loan as Wealth Tool", d: `₹${(homeLoanEMI / 1000).toFixed(0)}k EMI builds equity + joint tax deduction (₹${(totalHomeDeduction / 100000).toFixed(1)}L/year combined).`, i: '/icons/home copy.png' });
        } else {
            coupleStrengths.push({ t: "Zero Home Loan Liabilities", d: "No major home loan liabilities, giving high capacity to save and invest.", i: '/icons/home copy.png' });
        }

        // 4. Credit Card Debt
        const ccOS = parseFloat(sectionF.creditCardOutstanding) || 0;
        const ccEMI = parseFloat(sectionC.creditCardEMI) || parseFloat(sectionF.creditCardEMI) || 0;
        const hasNoCCDebt = ccOS === 0 && ccEMI === 0;
        if (hasNoCCDebt) {
            coupleStrengths.push({ t: "No Credit Card Debt", d: "You carry no high-interest revolving debt. Excellent.", i: '/icons/rupees.png' });
        } else {
            coupleStrengths.push({ t: "Credit Card Action Plan", d: `Resolve ₹${ccOS.toLocaleString('en-IN')} outstanding card dues to save interest.`, i: '/icons/rupees.png' });
        }

        // 5. Employer Health Cover
        const hCoverVal = parseFloat(sectionE.husbandHealthCover) || 0;
        const wCoverVal = parseFloat(sectionE.wifeHealthCover) || 0;
        const combinedCover = hCoverVal + wCoverVal;
        if (combinedCover > 0) {
            coupleStrengths.push({ t: "Employer Health Insurance", d: `₹${(combinedCover / 100000).toFixed(0)}L combined base cover (needs family floater top-up).`, i: '/icons/insurance copy.png' });
        } else {
            coupleStrengths.push({ t: "Family Health Protection", d: "Active health insurance plan is running to shield family savings.", i: '/icons/insurance copy.png' });
        }
    } else {
        // Build dynamic parts
        const homeLoanPart = hasHomeLoan ? 'owns a home (with an active loan)' : '';
        const healthInsurancePart = hasHealthInsurance ? `protects ${possessivePronoun.toLowerCase()} family with health insurance` : '';
        const investingPart = hasInvestments ? 'has already begun investing' : '';

        // Combine parts with proper connectors
        let storyParts = [];
        if (homeLoanPart) storyParts.push(homeLoanPart);
        if (healthInsurancePart) storyParts.push(healthInsurancePart);
        if (investingPart) storyParts.push(investingPart);

        let storyConnector = '';
        if (storyParts.length === 1) {
            storyConnector = storyParts[0];
        } else if (storyParts.length === 2) {
            storyConnector = `${storyParts[0]} and ${storyParts[1]}`;
        } else if (storyParts.length === 3) {
            storyConnector = `${storyParts[0]}, ${storyParts[1]}, and ${storyParts[2]}`;
        }

        // Determine foundation text based on what user has
        const foundationText = storyParts.length > 0
            ? `Starting from this foundation, the overall financial health score today is ${grades.overall || "60"}/100.`
            : `Starting ${possessivePronoun.toLowerCase()} financial journey today, the overall financial health score is ${grades.overall || "60"}/100.`;

        userSummaryText = `${pNameForSummary} is a ${p.age || "30"}-year-old ${p.incomeType === 'business' ? 'business owner' : 'salaried professional'} earning ₹${formatCurrency(metrics.totalMonthlyIncome)} per month. ${genderPronoun} ${storyConnector ? storyConnector + '.' : 'is building a financial foundation.'} ${foundationText} This report reveals exactly how to move to 80+ — without earning more or sacrificing lifestyle.`;
    }

    const wrappedSummaryText = doc.splitTextToSize(userSummaryText, pageWidth - 42);
    const boxHeight = Math.max(45, (wrappedSummaryText.length * 6.5) + 18); // Dynamic height calculation

    // Narrative Box (Dynamic)
    doc.setDrawColor(6, 150, 104); doc.setLineWidth(0.3);
    doc.roundedRect(14, 38, pageWidth - 28, boxHeight, 6, 6, 'S');
    doc.setTextColor(6, 150, 104); doc.setFontSize(11); doc.setFont("Montserrat", "bold");
    doc.text(isCouple ? "Your Family Story in 60 Seconds" : "Your Story in 60 Seconds", 20, 48);
    doc.setTextColor(30, 41, 59); doc.setFontSize(12.5); doc.setFont("Montserrat", "normal");
    doc.text(wrappedSummaryText, 20, 56, { align: 'left', lineHeightFactor: 1.5 });

    // Calculate Y position for next section
    let currentY = 38 + boxHeight + 12;

    // Strengths Heading (Shifted dynamically)
    doc.setFillColor(6, 150, 104); doc.rect(14, currentY - 6, 2.5, 8, 'F');
    doc.setTextColor(6, 150, 104); doc.setFontSize(12); doc.setFont("Montserrat", "bold");
    doc.text(isCouple ? "What Is Already Working (Family Strengths)" : "What Is Already Working (5 Real Strengths)", 20, currentY);

    // --- Dynamic strengths based on actual user data ---
    const lifestylePct = parseFloat((metrics.lifestyleRatio || 0).toFixed(1));
    const lifestyleDesc = lifestylePct > 0
        ? `spending only ${lifestylePct}% on lifestyle — well within the healthy 15% benchmark.`
        : `minimal lifestyle spending recorded — showing exceptional financial discipline.`;

    const s80cPct = (metrics.tesPercent || 0).toFixed(0);

    // Health insurance cover string
    const selfHCover = parseFloat(report?.salaryIncome?.healthInsuranceCover) || metrics.healthInsuranceCover || 0;
    const parentHCover = parseFloat(report?.salaryIncome?.parentHealthInsuranceCover) || 0;
    const hCoverStr = selfHCover > 0
        ? `₹${(selfHCover / 100000).toFixed(0)}L cover active for self`
        : 'health cover active';
    const parentStr = parentHCover > 0 ? ` and ₹${(parentHCover / 100000).toFixed(0)}L for parents` : (personalInfo.parentHealthInsurance === 'yes' ? ' and parents' : '');

    // Investments list
    const activeInvestments = [];
    if ((parseFloat(report?.salaryInvestment?.mutualFunds) || 0) > 0) activeInvestments.push('SIPs');
    if ((parseFloat(report?.salaryIncome?.ppf) || 0) > 0) activeInvestments.push('PPF');
    if ((parseFloat(report?.salaryIncome?.epf) || 0) > 0) activeInvestments.push('EPF');
    if ((parseFloat(report?.salaryIncome?.nps_80ccd2) || 0) > 0) activeInvestments.push('NPS');
    if ((parseFloat(report?.salaryInvestment?.stocksEquity) || 0) > 0) activeInvestments.push('Stocks');
    if ((parseFloat(report?.salaryInvestment?.goldPhysical) || 0) > 0) activeInvestments.push('Gold');

    const investStr = activeInvestments.length > 0
        ? `${activeInvestments.join(', ')} running.`
        : 'Starting early with investments is your biggest advantage.';

    const totalMonthlyInvestments = Math.round(metrics.totalMonthlyInvestments || 0);

    let strengthsList = [];
    if (isCouple) {
        strengthsList = coupleStrengths.map((cs, idx) => ({
            n: (idx + 1).toString(),
            t: cs.t + ":",
            d: cs.d,
            i: idx === 0 ? '/icons/investing_new.png' :
                idx === 1 ? '/icons/lifestyle_new.png' :
                    idx === 2 ? '/icons/home_new.png' :
                        idx === 3 ? '/icons/tax_new.png' :
                            '/icons/family_new.png'
        }));
    } else {
        strengthsList = [
            {
                n: "1",
                t: "Lifestyle discipline:",
                d: lifestyleDesc,
                i: '/icons/lifestyle_new.png'
            },
            {
                n: "2",
                t: "Health protection:",
                d: `${hCoverStr}${parentStr}. Smart protection against medical emergencies.`,
                i: '/icons/family_new.png'
            },
            hasHomeLoan
                ? {
                    n: "3",
                    t: "Home equity building:",
                    d: hEMI > 0
                        ? `EMI of ₹${hEMI.toLocaleString('en-IN')} is creating approx ₹${Math.round(hEMI * 0.4).toLocaleString('en-IN')} equity every month in your asset.`
                        : `Home loan of ₹${(hOut / 100000).toFixed(1)}L is building your primary long-term asset.`,
                    i: '/icons/home_new.png'
                }
                : {
                    n: "3",
                    t: "Savings foundation:",
                    d: totalMonthlyInvestments > 0
                        ? `Investing ₹${totalMonthlyInvestments.toLocaleString('en-IN')} per month — a strong financial base is forming.`
                        : `Your existing savings & assets form a strong foundation for future growth.`,
                    i: '/icons/home_new.png'
                },
            {
                n: "4",
                t: "80C tax strategy:",
                d: `${s80cPct}% of ₹1.5L tax-saving limit utilised. You are keeping more of what you earn.`,
                i: '/icons/tax_new.png'
            },
            {
                n: "5",
                t: "Investing habit:",
                d: investStr,
                i: '/icons/investing_new.png'
            }
        ];
    }

    let currentSy = currentY + 10;
    strengthsList.forEach(item => {
        const titleText = item.t;
        const descText = item.d;
        const iconX = pageWidth - 28;

        // Calculate available width for description
        doc.setFont("Montserrat", "bold"); doc.setFontSize(12.5);
        const tWidth = doc.getTextWidth(titleText);
        const descStartX = 32 + tWidth + 2;
        const availDescWidth = iconX - descStartX - 12; // buffer to avoid touching icon

        doc.setFont("Montserrat", "normal");
        const descLines = doc.splitTextToSize(descText, availDescWidth);

        // Dynamic Height (Base 14mm, add 5.5mm per extra line)
        const cardH = descLines.length > 1 ? 14 + (descLines.length - 1) * 5.5 : 14;
        const midY = currentSy + (cardH / 2);

        // Draw Box
        doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3);
        doc.roundedRect(14, currentSy, pageWidth - 28, cardH, 7, 7, 'S');

        // Number Badge (Vertically Centered)
        doc.setFillColor(6, 150, 104); doc.circle(22, midY, 5, 'F');
        doc.setTextColor(255, 255, 255); doc.setFont("Montserrat", "bold"); doc.setFontSize(12.5);
        doc.text(item.n, 22, midY + 1.5, { align: 'center' });

        // Title (Bold Green)
        doc.setTextColor(6, 150, 104); doc.setFont("Montserrat", "bold");
        doc.text(titleText, 32, currentSy + 8.5);

        // Description (Multi-line wrapping)
        doc.setTextColor(30, 41, 59); doc.setFont("Montserrat", "normal");
        doc.text(descLines, descStartX, currentSy + 8.5, { lineHeightFactor: 1.1 });

        // Icon (Vertically Centered)
        try { doc.addImage(item.i, 'PNG', iconX, midY - 5, 10, 10); } catch (e) { }

        currentSy += cardH + 4; // 4mm gap between cards
    });


    addFooter(doc, "01", name, pageWidth, pageHeight);

    // --- PAGE 04: THE 5 GAPS ---
    doc.addPage();
    renderFiveGapsPage(doc, { name, m, metrics, pageWidth, pageHeight, report, calculations });
    addFooter(doc, "02", name, pageWidth, pageHeight);

    // --- PAGE 05: FINANCIAL SNAPSHOT ---
    doc.addPage();
    renderSnapshotPage(doc, { name, m, metrics, pageWidth, pageHeight, personalInfo, report, calculations });
    addFooter(doc, "03", name, pageWidth, pageHeight);

    // --- PAGE 06: FINANCIAL REPORT CARD (Diagnosis) ---
    doc.addPage();
    renderDiagnosisPage(doc, { name, grades, m, metrics, pageWidth, pageHeight, report, calculations });
    // Footer is now inside renderDiagnosisPage as Page 04

    // --- PAGE 07: STRENGTHS PART 1 ---
    doc.addPage();
    renderStrengthsPage1(doc, { name, m, metrics, pageWidth, pageHeight, personalInfo, report });
    addFooter(doc, "05", name, pageWidth, pageHeight);

    // --- PAGE 08: STRENGTHS PART 2 ---
    doc.addPage();
    renderStrengthsPage2(doc, { name, m, metrics, pageWidth, pageHeight, personalInfo, report });
    addFooter(doc, "06", name, pageWidth, pageHeight);

    // --- PAGE 12: ROADMAP ---
    doc.addPage();
    renderRoadmapPage(doc, { name, lifeInsuranceTargetCr, taxSavingsMonthly, personalLoanEMI, pageWidth, pageHeight, calculations, report });
    addFooter(doc, "07", name, pageWidth, pageHeight);

    // --- PAGE 13: SAVINGS PLAN OR OPTIMISED SAVINGS & INVESTMENT PLAN ---
    const interestSaved = Math.round((personalLoanBalance || 0) * 0.14); // Estimated interest saved

    if (isCouple) {
        // --- PAGE 13: OPTIMISED SAVINGS & INVESTMENT PLAN (SECTION 6) ---
        doc.addPage();
        renderSavingsOptimisationPage(doc, { name, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, emergencyFundTarget, pageWidth, pageHeight, report });

        // --- PAGE 14: JOINT TAX OPTIMISATION STRATEGY ---
        doc.addPage();
        renderJointTaxOptimisationPage(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report });

        // --- PAGE 15: JOINT DEBT & GOALS PLAN (SECTIONS 8 & 9) ---
        doc.addPage();
        renderJointDebtAndGoalsPage(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, pageNum: "10" });
    } else {
        // --- PAGE 13: SAVINGS PLAN ---
        doc.addPage();
        renderSavingsPlanPage(doc, { name, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, personalLoanBalance, interestSaved, pageWidth, pageHeight, report });
        addFooter(doc, "08", name, pageWidth, pageHeight);

        // --- PAGE 14: TIMELINE & MILESTONES ---
        doc.addPage();
        renderTimelinePage(doc, { name, calculations, metrics, taxSavings, personalLoanEMI, emergencyFundTarget, pageWidth, pageHeight, report });

        // --- PAGE 15: OPTIMISED SAVINGS & INVESTMENT PLAN ---
        doc.addPage();
        renderSavingsOptimisationPage(doc, { name, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, emergencyFundTarget, pageWidth, pageHeight, report });
    }

    // --- PAGE 16: WEALTH & LIABILITIES OVERVIEW (SECTION 7) ---
    doc.addPage();
    renderWealthLiabilitiesPage(doc, { name, m, metrics, calculations, taxSavingsMonthly, personalLoanEMI, pageWidth, pageHeight, report });

    // --- PAGE 17: GOAL-BASED INVESTMENT PLAN (SECTION 08) ---
    if (!isCouple) {
        doc.addPage();
        renderGoalBasedInvestmentPlan(doc, { name, metrics, calculations, personalInfo, pageWidth, pageHeight, report });
        addSimpleFooter(doc, "13", pageWidth, pageHeight);
    }
    // --- PAGE 20: FINANCIAL GLOSSARY (SECTION 11) ---
    doc.addPage();
    renderGlossaryPage(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report });
    addSimpleFooter(doc, "15", pageWidth, pageHeight);

    if (!isCouple) {
        // --- PAGE 21: FINANCIAL GLOSSARY CONT. (SECTION 11) ---
        doc.addPage();
        renderGlossaryPage2(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report });
        addSimpleFooter(doc, "16", pageWidth, pageHeight);

        // --- PAGE 22: FINANCIAL GLOSSARY CONT. (SECTION 11) ---
        doc.addPage();
        renderGlossaryPage3(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, rentPaid, taxSavings, age: personalInfo.age || "", gender: personalInfo.gender || "professional", emergencyTarget: emergencyFundTarget, currentLifeCover: lifeInsuranceActual });
        addSimpleFooter(doc, "17", pageWidth, pageHeight);

        // --- PAGE 23: FINANCIAL GLOSSARY CONT. (SECTION 11) ---
        doc.addPage();
        renderGlossaryPage5(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, age: personalInfo.age || "" });
        addSimpleFooter(doc, "18", pageWidth, pageHeight);

        // --- PAGE 24: FINANCIAL GLOSSARY CONT. (SECTION 11) ---
        doc.addPage();
        renderGlossaryPage4(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report, age: personalInfo.age || "" });
        addSimpleFooter(doc, "19", pageWidth, pageHeight);

        // --- PAGE 25: FINANCIAL GLOSSARY CONT. (SECTION 11) ---
        doc.addPage();
        renderGlossaryPage6(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report });
        addSimpleFooter(doc, "20", pageWidth, pageHeight);
    }

    // --- PAGE 26: FINAL WORD ---
    doc.addPage();
    renderFinalPage(doc, { name, m, metrics, retirementCorpusCr, pageWidth, pageHeight, calculations, report });
    addFooter(doc, isCouple ? "16" : "21", name, pageWidth, pageHeight);

    // --- PAGE 27: FINANCIAL HEALTH PARAMETERS ---
    doc.addPage();
    renderParametersPage(doc, { name, m, metrics, calculations, pageWidth, pageHeight, report });
    addSimpleFooter(doc, isCouple ? "17" : "22", pageWidth, pageHeight);
    // --- PAGE 28: IMPORTANT DISCLAIMER ---
    doc.addPage();
    renderDisclaimerPage(doc, { pageWidth, pageHeight, isCouple });

    // --- PAGE 29: 5-YEAR ROADMAP SUMMARY ---
    doc.addPage();
    renderRoadmapSummaryPage(doc, { name, pageWidth, pageHeight, calculations, metrics, report });

    // Verify all pages are created
    const totalPages = doc.internal.getNumberOfPages();
    console.log(`PDF Generated with ${totalPages} pages`);

    // Save or Open PDF
    const filename = `Yathartha_Diagnosis_${(name || 'Report').replace(/\s+/g, '_')}.pdf`;

    try {
        if (isMobile) {
            // Mobile-optimized PDF download - ensure all pages are included
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const isAndroid = /Android/.test(navigator.userAgent);

            if (isIOS) {
                // For iOS: Use Blob URL for better PDF rendering
                const pdfArrayBuffer = doc.output('arraybuffer');
                const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
                const pdfUrl = URL.createObjectURL(pdfBlob);

                // Open in new tab
                const newTab = window.open(pdfUrl, '_blank');

                if (newTab) {
                    // Show instructions alert after a delay
                    setTimeout(() => {
                        alert(`📄 PDF Opened in New Tab!\n\n🔐 Password: ${userPassword}\n(Last 4 digits of your phone)\n\n💾 To Save:\n1. In new tab, tap Share button (⬆️)\n2. Select "Save to Files"\n\n📱 To Open Later:\nEnter password: ${userPassword}`);
                    }, 1500);
                } else {
                    // Popup blocked - try direct location change
                    window.location.href = pdfUrl;
                    setTimeout(() => {
                        alert(`📄 PDF Opened!\n\n🔐 Password: ${userPassword}\n\n💾 Tap Share (⬆️) → Save to Files`);
                    }, 1000);
                }

                // Cleanup blob URL after some time
                setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
            } else if (isAndroid) {
                // For Android: Use complete blob with proper mime type
                const pdfArrayBuffer = doc.output('arraybuffer');
                const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(pdfBlob);

                // Create download link
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.type = 'application/pdf';
                document.body.appendChild(link);

                // Trigger download
                link.click();

                // Cleanup
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                }, 3000);

                // Show alert with password info
                setTimeout(() => {
                    alert(`📄 PDF Downloaded Successfully!\n\n🔐 Password: ${userPassword}\n(Last 4 digits of your phone)\n\n💾 Check your Downloads folder.\n\n📱 To Open:\nEnter password: ${userPassword}`);
                }, 500);
            } else {
                // Other mobile devices
                doc.save(filename);
            }
        } else {
            // Desktop: Use native save
            doc.save(filename);
        }
    } catch (saveErr) {
        console.error("PDF_SAVE_ERROR:", saveErr);
        // Ultimate fallback - use doc.save which is most reliable
        try {
            doc.save(filename);
        } catch (fallbackErr) {
            console.error("PDF_FALLBACK_ERROR:", fallbackErr);
            alert('PDF generation failed. Please try the "View Report" option instead.');
        }
    }
};
